import React from "react";
import { Input, Button, Space } from "antd";
import { formatCurrencyWithConversion } from "../../../../utils/currencyUtils";

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
const defaultParseNumber = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "")
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};

// Column definitions for invoice table with allocation functionality
const getInvoiceColumns = ({
  handleOverpaymentChange,
  handleAllocateOverpayment,
  allocations,
  loadingAllocation,
  remainingAmount,
  parseNumberFn = defaultParseNumber,
  onViewPartner,
}) => [
  {
    title: "Invoice #",
    dataIndex: "number",
    key: "number",
    width: 120,
    ellipsis: true,
    sorter: (a, b) => (a.number || "").localeCompare(b.number || ""),
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input placeholder="Search invoice #" value={selectedKeys[0]} onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])} onPressEnter={confirm} style={{ marginBottom: 8, display: 'block' }} />
        <Space><Button type="primary" onClick={confirm} size="small">Search</Button><Button onClick={clearFilters} size="small">Reset</Button></Space>
      </div>
    ),
    onFilter: (value, record) => (record.number || "").toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Partner",
    dataIndex: "partner_name",
    key: "partner_name",
    width: 150,
    ellipsis: true,
    render: (text, record) => {
      // Get partner name from various possible fields
      const name = text || record?.partner?.name || "-";
      // Get partner ID from various possible fields
      const partnerId = record?.partner_id || record?.partner?.id || (typeof record?.partner === "number" ? record?.partner : null);
      if (partnerId && onViewPartner) {
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onViewPartner(partnerId);
            }}
            style={{ color: "#1890ff", cursor: "pointer" }}
            title="View partner transactions"
          >
            {name}
          </a>
        );
      }
      return name;
    },
  },
  {
    title: "Issue Date",
    dataIndex: "invoice_date",
    key: "invoice_date",
    width: 100,
  },
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "due_date",
    width: 100,
  },
  {
    title: "Total",
    dataIndex: "total_amount",
    key: "total_amount",
    width: 110,
    render: (amount, record) => {
      const currency =
        record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Paid",
    dataIndex: "paid_amount",
    key: "paid_amount",
    width: 110,
    render: (amount, record) => {
      const currency =
        record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Remaining",
    dataIndex: "remainingAmount",
    key: "remainingAmount",
    width: 110,
    render: (amount, record) => {
      const currency =
        record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Allocate Amt",
    dataIndex: "overpaymentAmount",
    key: "overpaymentAmount",
    width: 130,
    render: (text, record) => {
      return (
        <Input
          type="text"
          size="small"
          value={text}
          onChange={(e) => handleOverpaymentChange(record.id, e)}
          disabled={loadingAllocation}
          style={{ width: 110 }}
          placeholder="Enter amount"
        />
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    width: 100,
    fixed: "right",
    render: (_, record) => {
      // Check if this invoice has an existing allocation
      const existingAllocation =
        allocations && Array.isArray(allocations)
          ? allocations.find(
              (alloc) =>
                alloc.invoice === record.id || alloc.invoice_id === record.id
            )
          : null;

      // Get the allocation amount if it exists
      const allocationAmount = existingAllocation
        ? parseNumberFn(existingAllocation.amount || 0)
        : 0;

      // Get the current input value (may be a string with commas)
      const currentAmount = record.overpaymentAmount;

      // Convert to number for comparison (handle commas)
      const numericAmount = parseNumberFn(currentAmount);

      // Button text based on action type
      let buttonText = "Allocate";
      let buttonType = "primary";

      if (existingAllocation) {
        if (numericAmount > 0 && numericAmount !== allocationAmount) {
          buttonText = "Update";
          buttonType = "primary";
        } else {
          buttonText = "Unallocate";
          buttonType = "danger";
        }
      }

      // Determine if this is an allocation or deallocation action
      const isAllocation =
        !existingAllocation ||
        (numericAmount > 0 && numericAmount !== allocationAmount);
      const isDeallocation = existingAllocation && buttonText === "Unallocate";

      // Use absolute values for comparison (handles both debit and credit overpayments)
      const sourceRemaining = Math.abs(parseNumberFn(remainingAmount || 0));
      const invoiceRemaining = parseNumberFn(record.remainingAmount || 0);

      // Determine if button should be disabled
      const isDisabled =
        loadingAllocation ||
        // Check approval status - only allow allocation if approved (for new allocations only)
        (isAllocation &&
          record.approval_status &&
          record.approval_status !== "approved") ||
        (isAllocation && (!numericAmount || numericAmount <= 0)) ||
        (isAllocation &&
          numericAmount > Math.min(invoiceRemaining, sourceRemaining));

      return (
        <Button
          type={buttonType}
          size="small"
          onClick={() => {
            handleAllocateOverpayment(
              record.id,
              isDeallocation ? 0 : numericAmount,
              existingAllocation ? existingAllocation.id : null
            );
          }}
          disabled={isDisabled}
          loading={loadingAllocation}
          title={
            isAllocation &&
            record.approval_status &&
            record.approval_status !== "approved"
              ? `Cannot allocate: Invoice must be approved (Current status: ${record.approval_status})`
              : undefined
          }
        >
          {buttonText}
        </Button>
      );
    },
  },
];

export default getInvoiceColumns;
